import React from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import ChartRenderer from './ChartRenderer';
import StructuredChart from './StructuredChart';

/**
 * EnhancedMarkdownRenderer - Renders markdown with chart detection
 * Detects code blocks and ASCII charts, renders them as visual charts
 */
export default function EnhancedMarkdownRenderer({ content, charts = [] }) {
  const chartMap = new Map((charts || []).map((chart) => [chart.id, chart]));

  const renderMarkdownHtml = (text, key) => {
    if (!text || text.length === 0) return null;
    const html = DOMPurify.sanitize(marked.parse(text));
    return (
      <div
        key={key}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };

  const renderStructuredChartsFromMarkdown = (text, keyPrefix) => {
    if (!text || text.length === 0) return [];

    const elements = [];
    const markerRe = /^\[CHART:([\w-]+)\]$/gm;
    let lastIndex = 0;
    let match;

    while ((match = markerRe.exec(text)) !== null) {
      const before = text.slice(lastIndex, match.index);
      if (before.length > 0) {
        const markdownElement = renderMarkdownHtml(before, `${keyPrefix}-md-${lastIndex}`);
        if (markdownElement) elements.push(markdownElement);
      }

      const chartId = match[1];
      const chart = chartMap.get(chartId);
      if (chart) {
        elements.push(<StructuredChart key={`${keyPrefix}-chart-${chartId}-${match.index}`} chart={chart} />);
      } else {
        const markerElement = renderMarkdownHtml(match[0], `${keyPrefix}-missing-${match.index}`);
        if (markerElement) elements.push(markerElement);
      }

      lastIndex = match.index + match[0].length;
    }

    const after = text.slice(lastIndex);
    if (after.length > 0) {
      const markdownElement = renderMarkdownHtml(after, `${keyPrefix}-md-${lastIndex}`);
      if (markdownElement) elements.push(markdownElement);
    }

    return elements;
  };

  // Pad content lines in plaintext box-drawing blocks so right-side borders stay aligned
  const normalizeBoxDrawingBlock = (text) => {
    const BOX_CHAR_RE = /[\u2554\u2550\u2557\u2551\u255a\u255d\u250c\u2500\u2510\u2502\u2514\u2518\u251c\u2524\u252c\u2534\u253c]/;
    if (!BOX_CHAR_RE.test(text)) return text;
    const lines = text.split('\n');
    if (lines.length === 0) return text;
    const maxLen = Math.max(...lines.map((l) => l.length));
    if (!isFinite(maxLen) || maxLen === 0) return text;
    return lines
      .map((line) => {
        const last = line.slice(-1);
        if ((last === '\u2551' || last === '\u2502') && line.length < maxLen) {
          return line.slice(0, -1).padEnd(maxLen - 1) + last;
        }
        return line;
      })
      .join('\n');
  };

  const renderPart = (part) => {
    if (part.type === 'markdown') {
      return renderStructuredChartsFromMarkdown(part.content, part.key);
    }

    const markerMatches = [...part.content.matchAll(/^\[CHART:([\w-]+)\]$/gm)];
    if (markerMatches.length > 0) {
      return markerMatches.map((markerMatch, index) => {
        const chartId = markerMatch[1];
        const chart = chartMap.get(chartId);
        if (!chart) {
          return renderMarkdownHtml(markerMatch[0], `${part.key}-missing-${index}`);
        }
        return <StructuredChart key={`${part.key}-chart-${chartId}-${index}`} chart={chart} />;
      });
    }

    const partContent =
      part.lang === 'plaintext' ? normalizeBoxDrawingBlock(part.content) : part.content;
    return <ChartRenderer key={part.key} content={partContent} lang={part.lang} />;
  };

  // Pre-process: If "barchart" appears as inline text, split entries properly
  let preprocessed = content;
  
  // Debug: Log content length and first chars
  console.log('[EnhancedMD] Processing content, length:', content.length, 'first 100 chars:', content.substring(0, 100));
  
  // Debug: Log if we see "barchart" text anywhere
  if (/\bbarchart\b/i.test(content)) {
    console.log('[EnhancedMD] ✓ Found "barchart" keyword in content');
    console.log('[EnhancedMD] Content preview:', content.substring(content.indexOf('barchart'), content.indexOf('barchart') + 200));
  } else {
    console.log('[EnhancedMD] ✗ "barchart" keyword NOT found in this content');
  }
  
  // Normalize inline chart responses first (do NOT create code blocks here).
  // This makes agent variants like:
  //   "M/C c | ... M/C d | ..." and
  //   "barchart PARETO ...: d1 | ... d2 | ..."
  // become one-entry-per-line text, then the normal multiline detector wraps safely.
  const splitInlineEntries = (text) => {
    const candidates = text
      .split(/\)\s+(?=[A-Za-zM])/)
      .map((part, index, array) => {
        if (index < array.length - 1 && !part.trim().endsWith(')')) {
          return `${part.trim()})`;
        }
        return part.trim();
      })
      .filter(Boolean);

    const parsed = candidates.filter((candidate) => /\|\s*[█░\s]*[-+]?[\d.]+/.test(candidate));
    if (parsed.length >= 2) return parsed;

    const fallback = text
      .split(/\s+(?=[A-Za-z][^|\n]{0,50}\|\s*[█░\s]*[-+]?[\d.]+)/)
      .map((part) => part.trim())
      .filter((candidate) => /\|\s*[█░\s]*[-+]?[\d.]+/.test(candidate));

    return fallback;
  };

  const normalizedLines = [];
  let inExistingFence = false;
  const originalLines = preprocessed.split('\n');

  for (const line of originalLines) {
    const trimmed = line.trim();

    if (/^```/.test(trimmed)) {
      normalizedLines.push(line);
      inExistingFence = !inExistingFence;
      continue;
    }

    if (inExistingFence || !trimmed) {
      normalizedLines.push(line);
      continue;
    }

    if (/^#{1,6}\s/.test(trimmed) || /^\*/.test(trimmed) || /^-/.test(trimmed) || /^\d+\./.test(trimmed) || trimmed.startsWith('|')) {
      normalizedLines.push(line);
      continue;
    }

    if (/^barchart\b/i.test(trimmed)) {
      const payload = trimmed.replace(/^barchart\s*/i, '');
      const titleMatch = payload.match(/^(.{5,140}?):\s*(.+)$/);
      const title = titleMatch ? `${titleMatch[1].trim()}:` : null;
      const entriesSource = titleMatch ? titleMatch[2].trim() : payload;
      const entries = splitInlineEntries(entriesSource);

      if (entries.length > 0) {
        const result = [];
        if (title) result.push(title);
        result.push(...entries);
        console.log('[EnhancedMD] ✓ Normalized inline barchart line into', result.length, 'lines');
        normalizedLines.push(...result);
        continue;
      }

      normalizedLines.push(line);
      continue;
    }

    const possibleInlineEntries = splitInlineEntries(trimmed);
    if (possibleInlineEntries.length >= 2) {
      console.log('[EnhancedMD] ✓ Normalized inline chart line into', possibleInlineEntries.length, 'lines');
      normalizedLines.push(...possibleInlineEntries);
      continue;
    }

    normalizedLines.push(line);
  }

  preprocessed = normalizedLines.join('\n');

  // Existing fenced blocks already carry an explicit chart contract.
  // Remove them from the heuristic pass entirely, then restore them afterwards.
  const explicitFenceBlocks = [];
  const fenceProtectedContent = preprocessed.replace(/```[\s\S]*?```/g, (match) => {
    const token = `@@EXPLICIT_FENCE_${explicitFenceBlocks.length}@@`;
    explicitFenceBlocks.push(match);
    return token;
  });
  
  // Now do line-by-line detection for multi-line bar charts
  const lines = fenceProtectedContent.split('\n');
  const processedLines = [];
  let inBarChart = false;
  let barChartLines = [];
  let inCodeFence = false;

  const flushBarChartSection = (isEof = false) => {
    if (!inBarChart || barChartLines.length === 0) {
      return;
    }

    if (isEof) {
      console.log('[EnhancedMD] ✓ End of content, wrapping remaining', barChartLines.length, 'lines');
    } else {
      console.log('[EnhancedMD] ✓ Ending bar chart section,', barChartLines.length, 'lines');
      console.log('[EnhancedMD] ✓ First line:', barChartLines[0]?.substring(0, 80));
      console.log('[EnhancedMD] ✓ Last line:', barChartLines[barChartLines.length - 1]?.substring(0, 80));
    }

    if (barChartLines.length > 0 && /^\s*barchart\b/i.test(barChartLines[0])) {
      console.log(isEof ? '[EnhancedMD] ⚠ Detected inline barchart format at EOF, needs splitting' : '[EnhancedMD] ⚠ Detected inline barchart format, needs splitting');
      const inlineText = barChartLines.join(' ').replace(/^\s*barchart\b\s*/i, '').trim();
      console.log('[EnhancedMD] Inline text length:', inlineText.length);

      const entries = [];
      const parts = inlineText.split(/\)\s+(?=[A-ZM][^|]{0,40}\|)/);
      for (let idx = 0; idx < parts.length; idx++) {
        const part = parts[idx];
        const fullPart = part.endsWith(')') ? part : part + ')';
        const match = fullPart.match(/^([^|]+)\s*\|\s*([█░\s]*)\s*([-+]?[\d.]+)\s+(.+)$/);
        if (match) {
          const label = match[1].trim();
          const blocks = match[2].trim();
          const value = match[3];
          const rest = match[4].trim();
          const entry = `${label} | ${blocks} ${value} ${rest}`;
          entries.push(entry);
          console.log('[EnhancedMD] ✓ Split entry', idx + 1, ':', entry.substring(0, 60));
        }
      }

      if (entries.length > 0) {
        console.log(isEof ? '[EnhancedMD] ✓ Successfully split into ' + entries.length + ' entries at EOF' : '[EnhancedMD] ✓ Successfully split into ' + entries.length + ' entries');
        barChartLines = entries;
      } else {
        console.log(isEof ? '[EnhancedMD] ⚠ Failed to split at EOF, using original' : '[EnhancedMD] ⚠ Failed to split, using original');
      }
    }

    processedLines.push('```barchart');
    processedLines.push(...barChartLines);
    processedLines.push('```');
    console.log('[EnhancedMD] ✓ Wrapped', barChartLines.length, 'lines in code block');
    if (barChartLines.length > 0) {
      console.log('[EnhancedMD] ✓ Code block content sample:', barChartLines[0].substring(0, 100));
    }
    barChartLines = [];
    inBarChart = false;
  };
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    if (/^```/.test(trimmedLine)) {
      flushBarChartSection(false);
      processedLines.push(line);
      inCodeFence = !inCodeFence;
      continue;
    }

    if (inCodeFence) {
      processedLines.push(line);
      continue;
    }
    
    // Skip markdown tables: lines with 3+ pipes or starting with | and having multiple pipes
    const pipeCount = (line.match(/\|/g) || []).length;
    const isTableLine = pipeCount >= 3 || (line.trim().startsWith('|') && pipeCount >= 2);
    
    // Detect bar chart lines: has | followed by █ or ░ or numbers (but not tables)
    const hasBarPattern = /\|\s*[█░]*\s*[\d.]+/.test(line) && !line.startsWith('```') && !isTableLine;
    
    if (hasBarPattern && i < 5) {
      console.log('[EnhancedMD] Line', i, 'matches bar pattern:', line.substring(0, 60));
    }
    
    if (isTableLine && i < 5) {
      console.log('[EnhancedMD] Line', i, 'is a table, skipping:', line.substring(0, 60));
    }
    
    if (hasBarPattern) {
      if (!inBarChart) {
        // Starting a bar chart section
        console.log('[EnhancedMD] ✓ Starting bar chart section at line', i);
        inBarChart = true;
        barChartLines = [];
        // Check if previous lines contain a title
        // Look back up to 5 lines to handle agent output variations (blank lines, formatting changes)
        if (i > 0 && processedLines.length > 0) {
          let titleFound = false;
          const linesToCheck = Math.min(5, processedLines.length);
          
          for (let lookback = 1; lookback <= linesToCheck && !titleFound; lookback++) {
            const checkIdx = processedLines.length - lookback;
            if (checkIdx < 0) break;
            
            const checkLine = processedLines[checkIdx];
            const trimmed = checkLine.trim();
            
            // Skip blank lines and lines with bar characters
            if (trimmed.length > 0 && !checkLine.includes('█') && !checkLine.includes('░') && !checkLine.includes('```')) {
              // Never include markdown headings in chart code blocks; they can create heading-only chart fragments
              const isMarkdownHeading = /^\s*#{1,6}\s/.test(checkLine);
              if (isMarkdownHeading) {
                continue;
              }

              // Flexible title detection for agent variations:
              // 1. Contains keywords: CHART, PARETO, Analysis, Breakdown, Downtime
              // 2. Ends with colon (common title pattern)
              // 3. Contains "–" or "—" (dash separators in titles)
              const hasKeywords = /CHART|PARETO|Analysis|Breakdown|Downtime|Report/i.test(checkLine);
              const endsWithColon = trimmed.endsWith(':');
              const hasDashSeparator = /[–—]/.test(checkLine);
              const looksLikeTitle = (hasKeywords || endsWithColon || hasDashSeparator) && trimmed.length > 10;
              
              if (looksLikeTitle) {
                // Found title! Remove all lines from checkIdx onwards and add to barChartLines
                const removedLines = processedLines.splice(checkIdx);
                // Filter out blank lines and add non-blank lines
                for (const removedLine of removedLines) {
                  if (removedLine.trim().length > 0) {
                    barChartLines.push(removedLine);
                  }
                }
                console.log('[EnhancedMD] ✓ Including title (lookback=' + lookback + '):', checkLine.substring(0, 50));
                titleFound = true;
              }
            }
          }
        }
      }
      barChartLines.push(line);
      console.log('[EnhancedMD] ✓ Added line to bar chart:', line.substring(0, 80));
    } else {
      flushBarChartSection(false);
      processedLines.push(line);
    }
  }
  
  // Handle any remaining bar chart at end
  flushBarChartSection(true);
  
  const processedContent = processedLines.join('\n').replace(/@@EXPLICIT_FENCE_(\d+)@@/g, (_, index) => {
    return explicitFenceBlocks[Number(index)] || _;
  });
  const wrappedBlockCount = (processedContent.match(/```barchart/g) || []).length;
  console.log('[EnhancedMD] Wrapped barchart blocks in processedContent:', wrappedBlockCount);
  
  // Split content by fenced code blocks
  const parts = [];
  let currentIndex = 0;
  let searchIndex = 0;
  let extractedCodeBlockCount = 0;
  while (searchIndex < processedContent.length) {
    const blockStart = processedContent.indexOf('```', searchIndex);
    if (blockStart === -1) {
      break;
    }

    if (blockStart > currentIndex) {
      const textBefore = processedContent.substring(currentIndex, blockStart);
      parts.push({
        type: 'markdown',
        content: textBefore,
        key: `md-${currentIndex}`
      });
    }

    const blockEnd = processedContent.indexOf('```', blockStart + 3);
    if (blockEnd === -1) {
      // Unclosed fence: push content as a 'code' part so ChartRenderer (not marked) handles it.
      // Pushing as 'markdown' would give marked a raw ``` opener, causing it to wrap everything
      // in <pre><code> and swallowing sections 7–8 into the grey area.
      let unclosedContent = processedContent.substring(blockStart + 3);
      const unclosedLines = unclosedContent.split('\n');
      let unclosedLang = '';
      if (unclosedLines.length > 0 && /^[a-z]+\s*$/i.test(unclosedLines[0].trim())) {
        unclosedLang = unclosedLines[0].trim().toLowerCase();
        unclosedLines.shift();
        unclosedContent = unclosedLines.join('\n');
      }
      unclosedContent = unclosedContent.trim();
      if (unclosedContent) {
        extractedCodeBlockCount += 1;
        console.log('[EnhancedMD] ⚠ Unclosed code fence — treating as code part, lang=', unclosedLang, 'len=', unclosedContent.length);
        parts.push({ type: 'code', content: unclosedContent, lang: unclosedLang, key: `code-${blockStart}` });
      }
      currentIndex = processedContent.length;
      break;
    }

    let codeContent = processedContent.substring(blockStart + 3, blockEnd);
    const lines = codeContent.split('\n');
    let codeLang = '';
    if (lines.length > 1 && /^[a-z]+\s*$/i.test(lines[0])) {
      codeLang = lines[0].trim().toLowerCase();
      lines.shift();
      codeContent = lines.join('\n');
    }
    codeContent = codeContent.trim();

    if (!codeContent || codeContent.trim().length === 0) {
      const emptySource = processedContent.substring(blockStart, blockEnd + 3);
      console.log('[EnhancedMD] ⚠ Empty code block source:', emptySource.substring(0, 50).replace(/\n/g, '\\n'));
    }

    if (codeContent && codeContent.trim().length > 0) {
      extractedCodeBlockCount += 1;
      if (extractedCodeBlockCount <= 10) {
        console.log('[EnhancedMD] Extracted code block', extractedCodeBlockCount, 'lang=', codeLang, 'len=', codeContent.length, 'preview=', codeContent.substring(0, 80));
      }
      parts.push({
        type: 'code',
        content: codeContent,
        lang: codeLang,
        key: `code-${blockStart}`
      });
    } else {
      console.log('[EnhancedMD] ⚠ Skipping empty code block at index', blockStart);
    }

    currentIndex = blockEnd + 3;
    searchIndex = blockEnd + 3;
  }
  
  // Add remaining text as markdown
  if (currentIndex < processedContent.length) {
    parts.push({
      type: 'markdown',
      content: processedContent.substring(currentIndex),
      key: `md-${currentIndex}`
    });
  }
  console.log('[EnhancedMD] Parts summary: total=', parts.length, 'code=', parts.filter(p => p.type === 'code').length, 'markdown=', parts.filter(p => p.type === 'markdown').length);
  
  // If no code blocks found, render entire content as markdown
  if (parts.length === 0) {
    return <div className="agent-message-markdown">{renderStructuredChartsFromMarkdown(processedContent, 'md-all')}</div>;
  }
  
  // Render each part
  return (
    <div className="agent-message-markdown">
      {parts.flatMap((part) => renderPart(part)).filter(Boolean)}
    </div>
  );
}
